/**
 * Soft drag board — unit tray → map (Soft≠Live · 127 only).
 * Hosted under Command · Drag (primary) and Dispatch desk.
 */
import { useEffect, useState } from "react";
import { html, Ico } from "./ui.js";
import {
  softSceneList,
  SOFT_SCENES,
  glyphForSoftUnit,
  suggestSoftUnitId,
} from "./soft-fleet-scenes.js";
import { softMarkSvg } from "./soft-fleet-glyphs.js";

function emitSoftDrag(unitId) {
  try {
    window.__crocSoftDragUnit = unitId || null;
    window.dispatchEvent(
      new CustomEvent("croc-soft-drag-unit", { detail: { id: unitId || null } }),
    );
  } catch {
    /* */
  }
}

function emitMode(mode) {
  try {
    window.__crocSoftInteractMode = mode;
    window.dispatchEvent(
      new CustomEvent("croc-soft-interact-mode", { detail: { mode } }),
    );
  } catch {
    /* */
  }
}

/**
 * @param {{ fleet: any, liveDevices?: any[], toast?: function }} props
 */
export function SoftDispatchBoard({ fleet, liveDevices, toast }) {
  if (!fleet || !fleet.enabled || !fleet.state) return null;
  const s = fleet.state;
  const scenes = softSceneList();
  const mode = s.interactMode || "dispatch";
  const scene = s.scene || "perimeter";
  const sceneMeta = SOFT_SCENES[scene] || SOFT_SCENES.perimeter;
  const selected = (s.assets || []).find((a) => a.id === s.selectedId) || null;
  const people = (s.assets || []).filter((a) => a.kind === "person");
  const [swapFrom, setSwapFrom] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    emitMode(mode);
    return () => emitMode("dispatch");
  }, [mode]);

  const onPickMode = (m) => {
    fleet.setInteractMode && fleet.setInteractMode(m);
    toast &&
      toast(
        m === "place"
          ? "Place mode · click map"
          : m === "bearing"
            ? "Bearing mode · click map"
            : "Drag mode · drop on map pin",
        "go",
      );
  };

  const onScene = (id) => {
    fleet.setScene && fleet.setScene(id);
    toast && toast(`Scene · ${SOFT_SCENES[id]?.label || id}`, "go");
  };

  const onDragStart = (ev, id) => {
    try {
      ev.dataTransfer.setData("text/soft-unit", id);
      ev.dataTransfer.effectAllowed = "copyMove";
    } catch {
      /* */
    }
    fleet.selectUnit && fleet.selectUnit(id);
    emitSoftDrag(id);
  };

  const onDragEnd = () => emitSoftDrag(null);

  const onSwap = () => {
    if (!swapFrom || !selected || selected.kind !== "person") {
      toast && toast("Pick two people to swap", "warn");
      return;
    }
    fleet.reassign && fleet.reassign(swapFrom, selected.id);
    toast && toast("People swapped · Soft", "go");
    setSwapFrom("");
  };

  const onSurvey = () => {
    fleet.snapSurvey && fleet.snapSurvey(liveDevices || []);
    toast && toast("Edge pins mirrored Soft · no Live write", "go");
  };

  const onBearing = (deg) => {
    if (!selected) return;
    fleet.setBearing && fleet.setBearing(selected.id, deg);
  };

  const onSuggest = () => {
    const id = suggestSoftUnitId(s);
    if (!id) {
      toast && toast("No free Soft unit", "warn");
      return;
    }
    fleet.selectUnit && fleet.selectUnit(id);
    fleet.setInteractMode && fleet.setInteractMode("dispatch");
    try {
      window.dispatchEvent(new CustomEvent("croc-soft-focus", { detail: { id } }));
    } catch {
      /* */
    }
    toast && toast(`Picked ${id}`, "go");
  };

  return html`
    <section
      className="ops-drag"
      data-mode=${mode}
      data-scene=${scene}
      data-cmc-soft="skill-weave"
      style=${{ "--sc": sceneMeta.ink }}
      aria-label="Drag Soft units onto map">
      <div className="ops-drag__row ops-drag__scenes" role="group" aria-label="Scene">
        ${scenes.map(
          (sc) => html`
            <button
              key=${sc.id}
              type="button"
              className="ops-drag__chip"
              data-on=${scene === sc.id ? "1" : "0"}
              style=${{ "--sc": sc.ink }}
              onClick=${() => onScene(sc.id)}>
              ${sc.label}
            </button>
          `,
        )}
      </div>

      <div className="ops-drag__row ops-drag__modes" role="group" aria-label="Mode">
        ${[
          ["dispatch", "Drag"],
          ["place", "Place"],
          ["bearing", "Turn"],
        ].map(
          ([id, lab]) => html`
            <button
              key=${id}
              type="button"
              className="ops-drag__mode"
              data-on=${mode === id ? "1" : "0"}
              onClick=${() => onPickMode(id)}>
              ${lab}
            </button>
          `,
        )}
      </div>

      <p className="ops-drag__hint">
        ${mode === "dispatch"
          ? "Drag chip → Soft pin · or free pad"
          : mode === "place"
            ? "Select unit · click map"
            : "Select unit · set heading"}
      </p>

      <div className="ops-drag__tray" role="list" aria-label="Soft units">
        ${(s.assets || []).map((a) => {
          const skills = (a.skills || []).slice(0, 2).join(" · ");
          const gid = glyphForSoftUnit({
            id: a.id,
            kind: a.kind,
            skills: a.skills,
            scene,
          });
          const mark = softMarkSvg(gid, 20);
          return html`
            <button
              key=${a.id}
              type="button"
              role="listitem"
              className="ops-drag__unit"
              draggable=${true}
              data-kind=${a.kind}
              data-free=${a.free ? "1" : "0"}
              data-sel=${s.selectedId === a.id ? "1" : "0"}
              style=${{ "--unit": a.color || sceneMeta.ink }}
              onDragStart=${(e) => onDragStart(e, a.id)}
              onDragEnd=${onDragEnd}
              onClick=${() => {
                fleet.selectUnit && fleet.selectUnit(a.id);
                emitSoftDrag(null);
                try {
                  window.dispatchEvent(
                    new CustomEvent("croc-soft-focus", { detail: { id: a.id } }),
                  );
                } catch {
                  /* */
                }
              }}>
              <span
                className="ops-drag__mark"
                dangerouslySetInnerHTML=${{ __html: mark }}
                aria-hidden="true"></span>
              <span className="ops-drag__main">
                <b>${a.label || a.id}</b>
                <i>${skills || a.tier || a.kind || "—"}</i>
              </span>
              <span className="ops-drag__st">${a.free ? "READY" : "OUT"}</span>
            </button>
          `;
        })}
      </div>

      ${mode === "bearing" && selected
        ? html`
            <div className="ops-drag__brg">
              <span>${selected.label}</span>
              <input
                type="range"
                min="0"
                max="359"
                value=${Number.isFinite(selected.bearing) ? selected.bearing : 0}
                onInput=${(e) => onBearing(e.target.value)}
              />
              <b>${Math.round(Number.isFinite(selected.bearing) ? selected.bearing : 0)}°</b>
            </div>
          `
        : null}

      ${mode === "dispatch" && people.length >= 2
        ? html`
            <div className="ops-drag__swap">
              <select value=${swapFrom} onChange=${(e) => setSwapFrom(e.target.value)}>
                <option value="">Swap from…</option>
                ${people.map(
                  (p) => html`<option key=${p.id} value=${p.id}>${p.label}</option>`,
                )}
              </select>
              <span>→</span>
              <b>${selected && selected.kind === "person" ? selected.label : "pick"}</b>
              <button
                type="button"
                className="btn btn--xs btn--ghost"
                disabled=${busy}
                onClick=${() => {
                  setBusy(true);
                  try {
                    onSwap();
                  } finally {
                    setBusy(false);
                  }
                }}>
                Swap
              </button>
            </div>
          `
        : null}

      <div className="ops-drag__acts">
        <button type="button" className="btn btn--xs btn--ghost" onClick=${onSuggest}>
          <${Ico} d="spark" w=${11} /> Pick free
        </button>
        <button type="button" className="btn btn--xs btn--ghost" onClick=${onSurvey}>
          <${Ico} d="map" w=${11} /> Mirror edge
        </button>
      </div>
    </section>
  `;
}
